import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    Sizes,
    Colours,
    CardBanner,
    UnstyledList,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
    PageBehaviourButton,
    DefaultIntlFormatValues,
} from '@ratehub/base-ui';
import {
    CreditCardCTA,
    getCardBannerProps,
    isEarningsAPercentage,
    getSummarizedEarnings,
    CreditCardSynopsisShape,
    useApplyForCreditCardCallback,
    extractRhAnalyticsFromCreditCard,
} from '@ratehub/cc-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import { SIMPLE_CREDIT_CARD_COLUMN_TYPES } from '../definitions/SimpleCreditCardColumnTypes';
import ProductTableHeader from './ProductTableHeader';


function SimpleCreditCardProductTable({
    title,
    lastUpdatedDate,

    detailsColumnContentType = SIMPLE_CREDIT_CARD_COLUMN_TYPES.welcomeBonus,
    detailsColumnHeading,

    simpleCreditCards = [],

    ...otherProps
}) {
    const intl = useIntl();
    const applyForCreditCardCallback = useApplyForCreditCardCallback();

    function handleCCCallback(cardInfo) {
        applyForCreditCardCallback({ cardInfo });
    }


    return (
        <SimpleCreditCardTable
            borderRadius={BORDER_RADIUS}
            {...otherProps}
        >
            <If condition={title || lastUpdatedDate}>
                <ProductTableHeader
                    title={title}
                    lastUpdatedDate={lastUpdatedDate}
                />
            </If>
            <For
                each="simpleCard"
                of={simpleCreditCards
                    .map(simple => ({
                        ...simple,
                        cardBannerProps: getCardBannerProps({
                            giftCardOffer: simple.card.giftCardOffer,
                            hasReferralOffer: simple.card.hasReferralOffer,
                        }, intl),
                    }))
                }
                index="index"
            >
                <div
                    className={classNames('simple-cc-row rh-my-0_5 rh-pb-0_5', ANALYTICS_ATTRIBUTE_NAME)}
                    ref={self => self && (self[ANALYTICS_KEY] = extractRhAnalyticsFromCreditCard(simpleCard.card))}
                    key={simpleCard.card.id}
                >
                    {/* optional gift offer or referral banner */}
                    <If condition={simpleCard.cardBannerProps}>
                        <CardBanner
                            text={simpleCard.cardBannerProps.message}
                            variant={simpleCard.cardBannerProps.variant}
                            borderRadius={BORDER_RADIUS}
                            isCompact
                        />
                    </If>

                    {/* inner row: flex container */}
                    <div className={classNames('simple-cc-inner-row rh-display-flex rh-flex-wrap rh-px-1 rh-width-100p rh-box-sizing-border-box', {
                        'rh-pt-1': !simpleCard.cardBannerProps,
                        'rh-pt-0_5': simpleCard.cardBannerProps,
                    })}
                    >
                        {/* card image and name column */}
                        <div className="card-image-and-name rh-display-flex">
                            <div className="card-image">
                                <p
                                    className="rh-text-2xs rh-m-0 rh-p-0"
                                    data-test-name="featured-annotation"
                                >
                                    <FormattedMessage
                                        id="web-components.edb.cc.simple.featured"
                                        defaultMessage={
                                            `{isSponsored, select, true {sponsored}
                                                other {{isMonetized, select, true {featured} other {}}}
                                            }`
                                        }
                                        values={{
                                            // for Sponsored, use only what the CMS author has specified
                                            isSponsored: simpleCard.isSponsored,
                                            isMonetized: simpleCard.card.isMonetized,
                                        }}
                                    />
                                </p>
                                <button
                                    className="rh-display-block rh-p-0 rh-m-auto rh-border-width-0"
                                    onClick={() => handleCCCallback(simpleCard.card)}
                                    data-name="edb.cc.simple.card-name-image"
                                    type="button"
                                >
                                    <img
                                        src={simpleCard.card.cardImageURL}
                                        alt={simpleCard.card.name}
                                        width={CARD_WIDTH}
                                        height={CARD_HEIGHT}
                                    />
                                </button>
                            </div>
                            <h3 className="card-name rh-my-0 rh-ml-0_75 rh-pt-0_75 rh-pr-1">
                                <PageBehaviourButton
                                    className="rh-title-3xs rh-m-0 rh-text-align-left weight-bold"
                                    onClick={() => handleCCCallback(simpleCard.card)}
                                    message={simpleCard.card.name}
                                    shouldLowerCaseMessage={false}
                                    rel="noopener"
                                    target="blank"
                                    data-name="edb.cc.simple.card-name-link"
                                />
                            </h3>
                        </div>

                        {/* annual fee column */}
                        <div className="card-annual-fee rh-display-flex rh-pt-0_75">
                            <h3 className="card-fee-label rh-title-3xs rh-m-0">
                                <FormattedMessage
                                    id="web-components.edb.cc.simple.annual-fee"
                                    defaultMessage="Annual fee"
                                />
                            </h3>

                            <p className="card-fee-text rh-text-s rh-m-0 rh-mr-0_5 rh-display-flex rh-flex-wrap">
                                <FormattedMessage
                                    id="web-components.edb.cc.simple.annual-fee.value"
                                    defaultMessage={
                                        `{isFirstYearFeeWaived, select,
                                            true {<line-through>{annualFee, number, CAD0}</line-through>
                                                <green>{zero, number, CAD0} first year</green>}
                                            other {{annualFee, number, CAD0}}}`
                                    }
                                    values={{
                                        isFirstYearFeeWaived: simpleCard.card.isFirstYearFeeWaived,
                                        annualFee: simpleCard.card.annualFee,
                                        zero: 0,
                                        green: str => <span key={str} className="rh-fg-lime">{str}</span>,  // eslint-disable-line
                                        ...DefaultIntlFormatValues,
                                    }}
                                />
                            </p>
                        </div>

                        {/* card details column */}
                        <div
                            className="card-details-column rh-pt-0_75"
                        >
                            <SimpleDetailsColumn
                                detailsColumnContentType={detailsColumnContentType}
                                detailsColumnHeading={detailsColumnHeading}
                                simpleCard={simpleCard}
                            />
                        </div>

                        {/* CTA column */}
                        <div className="card-cta rh-display-flex rh-flex-direction-column rh-align-items-center rh-justify-content-center rh-my-0_5">
                            <CreditCardCTA
                                cardTypes={simpleCard.card.cardTypes}
                                giftCardOffer={simpleCard.card.giftCardOffer}
                                hasReferralOffer={simpleCard.card.hasReferralOffer}
                                cardId={simpleCard.card.id}

                                hasEligibilityCTA={false}
                                onClick={() => handleCCCallback(simpleCard.card)}
                            />
                        </div>
                    </div>
                </div>
            </For>
        </SimpleCreditCardTable>
    );
}

const SimpleCardShape = PropTypes.shape({
    card: PropTypes.shape(CreditCardSynopsisShape),
    isSponsored: PropTypes.bool,
    detailsColumnText: PropTypes.string,
});

SimpleCreditCardProductTable.propTypes = {
    title: PropTypes.string,
    lastUpdatedDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
    ]),

    detailsColumnContentType: PropTypes.oneOf(Object.values(SIMPLE_CREDIT_CARD_COLUMN_TYPES)),
    detailsColumnHeading:  PropTypes.string,

    simpleCreditCards: PropTypes.arrayOf(SimpleCardShape),
};

function SimpleDetailsColumn({
    detailsColumnContentType,
    detailsColumnHeading,
    simpleCard,
}) {
    const detailsColumn = getDetailsColumn({
        detailsColumnContentType,
        detailsColumnHeading,
        simpleCard,
    });

    return (
        <>
            <h3
                className="rh-title-3xs rh-m-0"
                data-test-name="details-column-heading"
            >
                {detailsColumn.heading}
            </h3>

            {/* based on detailsColumnContentType, we’ll either use the custom per-card `detailsColumnText`
                as specified in the CMS, or one of the pre-programmed choices */}
            <div
                className={classNames('rh-text-s rh-pr-1', detailsColumn.text?.className)}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={detailsColumn.text?.dangerously}
                data-test-name="card-details-column"
            >
                {detailsColumn.text?.content}
            </div>
        </>
    );
}

SimpleDetailsColumn.propTypes = ({
    detailsColumnContentType: PropTypes.oneOf(Object.values(SIMPLE_CREDIT_CARD_COLUMN_TYPES)).isRequired,
    detailsColumnHeading: PropTypes.string,
    simpleCard: SimpleCardShape.isRequired,
});

function getDetailsColumn({
    detailsColumnContentType,
    detailsColumnHeading,
    simpleCard,
}) {
    const cardInfo = simpleCard.card;

    let heading,
        text;

    switch (detailsColumnContentType) {
        case SIMPLE_CREDIT_CARD_COLUMN_TYPES.custom:
            heading = detailsColumnHeading;
            text = {
                className: 'custom-details-column',
                dangerously: { __html: simpleCard.detailsColumnText },
            };
            break;

        case SIMPLE_CREDIT_CARD_COLUMN_TYPES.welcomeBonus:
            heading = DETAILS_COLUMN_HEADINGS[SIMPLE_CREDIT_CARD_COLUMN_TYPES.welcomeBonus];
            text = {
                content: cardInfo.welcomeBonusDescription,
            };
            break;

        case SIMPLE_CREDIT_CARD_COLUMN_TYPES.rewardEarningRates: {
            heading = DETAILS_COLUMN_HEADINGS[SIMPLE_CREDIT_CARD_COLUMN_TYPES.rewardEarningRates];

            const isEarningsShownAsPercentage = isEarningsAPercentage({
                cardId: cardInfo.id,
                cardProductType: cardInfo.productType,
            });

            const earningSummary = getSummarizedEarnings(isEarningsShownAsPercentage, cardInfo.earningsDescription);

            text = {
                content: (
                    <UnstyledList
                        className="rh-mt-0"
                        listItems={earningSummary.summaryDetails}
                    />
                ),
            };

            break;
        }

        default:
            break;
    }

    return {
        heading,
        text,
    };
}


const BORDER_RADIUS = '2px';
const CARD_WIDTH = '90px';     // w x h ~ 1.6 x 1; height will be 56.25
const CARD_HEIGHT = parseInt(CARD_WIDTH) * 0.625 + 'px';

const DETAILS_COLUMN_HEADINGS = {
    [SIMPLE_CREDIT_CARD_COLUMN_TYPES.welcomeBonus]: (
        <FormattedMessage
            id="web-components.edb.cc.simple.details.welcome-bonus"
            defaultMessage="Welcome bonus"
        />
    ),

    [SIMPLE_CREDIT_CARD_COLUMN_TYPES.rewardEarningRates]: (
        <FormattedMessage
            id="web-components.edb.cc.simple.details.reward-earning-rates"
            defaultMessage="Earn rewards"
        />
    ),
};


const SimpleCreditCardTable = styled.div`
    .simple-cc-row {
        border-radius: ${BORDER_RADIUS};
        box-shadow: 1px 0 2px 1px ${Colours.STONE_LIGHT},
            0 1px 2px 1px ${Colours.STONE_LIGHT},
            -1px 0 2px 1px ${Colours.STONE_LIGHT},
            0 -1px 2px 1px ${Colours.STONE_LIGHT};
    }

    .simple-cc-inner-row {
        > .card-image-and-name {
            flex: 1 1 16rem;

            > .card-image {
                flex: 0 1 auto;
            }
            > .card-name {
                flex: 1 1 6rem;
            }
        }

        > .card-annual-fee {
            flex: 1 1 8rem;
            flex-wrap: wrap;
            align-content: flex-start;

            .card-fee-label {
                flex: 1 1 7rem;
            }
            .card-fee-text {
                flex: 1 1 12rem;

                > .rh-text-decoration-line-through {
                    flex: 0 1 2.5rem;
                }

                > .rh-fg-lime {
                    flex: 1 1 9rem;
                }
            }
        }

        > .card-details-column {
            flex: 1 1 28rem;

            /* card-specific custom markup entered in the CMS */
            > .custom-details-column {
                > p {
                    margin-top: 0;
                    margin-bottom: ${Sizes.SPACING.QUARTER};
                }

                > ul {
                    margin-top: 0;
                    padding-left: ${Sizes.SPACING.ONE};
                    margin-bottom: ${Sizes.SPACING.QUARTER};
                }
            }
        }

        > .card-cta {
            flex: 1 1 10rem;
        }
    }
`;


SimpleCreditCardProductTable.blockKey = 'rh/simple-credit-card-product-table';
SimpleCreditCardProductTable.requiresLayoutRow = true;
SimpleCreditCardProductTable.supportedPageTypes = [ PAGE_TEMPLATES.creditCards, PAGE_TEMPLATES.sandbox ];

export default observer(SimpleCreditCardProductTable);
